import {
  ChangeEvent,
  Dispatch,
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  Reducer,
} from 'react';
import { Schema, ValidationError } from 'yup';

// The secret key so that useForm and useField can know eachother's secrets.
// Shhhhhh.
export const CONTROLLER = Symbol();

// General helpers
function isValidationError(x: any): x is ValidationError {
  return x instanceof ValidationError;
}

function isPromise(obj: any): obj is Promise<any> {
  return (
    !!obj &&
    (typeof obj === 'object' || typeof obj === 'function') &&
    typeof obj.then === 'function'
  );
}

// Options for hydrating our form.
interface Options<T> {
  initialValues: T;
  onSubmit: (values: T) => void | Promise<void>;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  validateOnFirstRun?: boolean;
  validationSchema?: Schema<any>;
}

interface State<T> {
  committed: T;
  draft: T;
  submitting: boolean;
  dirty: Map<keyof T, boolean>;
  errors: Map<keyof T, string[]>;
  focused: Map<keyof T, boolean>;
  touched: Map<keyof T, boolean>;
}

type Action<T> =
  | { type: 'SUBMIT' }
  | { type: 'UPDATE'; payload: { name: keyof T; value: T[keyof T] } }
  | { type: 'FOCUS'; payload: { name: keyof T } }
  | { type: 'BLUR'; payload: { name: keyof T } }
  | { type: 'RESET' }
  | { type: 'COMMIT' }
  | {
      type: 'VALIDATE';
      payload: { state: 'ok' } | { state: 'error'; errors: Map<keyof T, string[]> };
    };

function reducer<T>(state: State<T>, action: Action<T>): State<T> {
  switch (action.type) {
    case 'UPDATE': {
      const { name, value } = action.payload;
      const currentDirtyState = state.dirty.get(name);
      const nextDirtyState = state.committed[name] !== value;
      const dirty =
        currentDirtyState !== nextDirtyState ? state.dirty.set(name, nextDirtyState) : state.dirty;
      const draft = { ...state.draft, [name]: value };

      return {
        ...state,
        dirty,
        draft,
      };
    }
    case 'FOCUS':
      return {
        ...state,
        focused: state.focused.set(action.payload.name, true),
      };
    case 'BLUR':
      return {
        ...state,
        focused: state.focused.set(action.payload.name, false),
        touched:
          // This is a mistake, it's accessing the global name object and not
          // the name in the payload
          // @ts-ignore
          state.touched.get(name) === true
            ? state.touched
            : state.touched.set(action.payload.name, true),
      };
    case 'VALIDATE':
      if (action.payload.state === 'error') {
        return {
          ...state,
          errors: action.payload.errors,
        };
      }

      if (state.errors.size === 0) {
        return state;
      }

      return {
        ...state,
        errors: new Map(),
      };
    case 'COMMIT':
      return {
        ...state,
        committed: { ...state.draft },
        submitting: false,
        dirty: new Map(),
        touched: new Map(),
        focused: new Map(),
        errors: new Map(),
      };
    case 'RESET':
      return {
        ...state,
        draft: { ...state.committed },
        dirty: new Map(),
        touched: new Map(),
        focused: new Map(),
        errors: new Map(),
      };
    case 'SUBMIT':
      return {
        ...state,
        submitting: true,
      };
    default:
      return state;
  }
}

export interface Form<T> {
  valid: boolean;
  dirty: boolean;
  submitting: boolean;
  handleSubmit: (event: FormEvent) => void | Promise<void>;
  handleReset: VoidFunction;

  [CONTROLLER]: {
    state: State<T>;
    dispatch: Dispatch<Action<T>>;
  };
}

// Provide a type that represents an object with key signatures.
type AnyObject = { [x: string]: any };

function useForm<T extends AnyObject>({
  initialValues,
  validationSchema,
  onSubmit,
  validateOnBlur = true,
  validateOnChange = true,
  validateOnFirstRun = true,
}: Options<T>): Form<T> {
  const [state, dispatch] = useReducer(reducer, {
    committed: initialValues,
    draft: initialValues,
    submitting: false,
    dirty: new Map(),
    errors: new Map(),
    focused: new Map(),
    touched: new Map(),
  });

  // Validates the form based on a yup validationSchema.
  function validate() {
    if (!validationSchema) {
      return;
    }
    try {
      validationSchema.validateSync(state.draft, { abortEarly: false });
      dispatch({ type: 'VALIDATE', payload: { state: 'ok' } });
    } catch (err) {
      if (isValidationError(err)) {
        // Assign all errors found by yup into the the error state.
        if (err.inner && err.inner.length > 0) {
          // Handle multiple errors
          const entries = err.inner.map(({ path, errors }) => {
            return [path, errors] as [string, string[]];
          });
          const errors = new Map(entries);

          dispatch({
            type: 'VALIDATE',
            payload: {
              errors,
              state: 'error',
            },
          });
        } else {
          // Handle single error
          const { path, message } = err;
          dispatch({
            type: 'VALIDATE',
            payload: {
              errors: new Map().set(path, message),
              state: 'error',
            },
          });
        }
      }
    }
  }

  // Provide a handler to be passed to the form consumer that allows rolling
  // back of all internal form state back to a pristine instance.
  const handleReset = useCallback(() => dispatch({ type: 'RESET' }), [dispatch]);

  // Provide a handler for saving the form. If saving is successful,
  // we should commit the current values as pristine, and roll back the
  // meta data fields by invoking handleReset().
  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      dispatch({ type: 'SUBMIT' });
      const submit = onSubmit(state.draft);

      if (isPromise(submit)) {
        try {
          await submit;
          dispatch({ type: 'COMMIT' });
        } catch (err) {
          // TODO: Handle error
        }
      } else {
        dispatch({ type: 'COMMIT' });
      }
    },
    [onSubmit, dispatch],
  );

  // If this is our first time around and we're given the option to do so,
  // lets validate the form.
  useEffect(() => {
    if (validateOnFirstRun) {
      validate();
    }
  }, []);

  // Implement an effect to validate everytime the values state changes if
  // we're told to do so. This is analogous to onChange.
  useEffect(() => {
    if (validateOnChange) {
      validate();
    }
  }, [state.draft]);

  // Implement an effect to validate everytime the touched object state changes
  // if we're told to do so. This is analogous to onBlur.
  useEffect(() => {
    if (validateOnBlur) {
      validate();
    }
  }, [state.touched]);

  // Memoize the entire form object that's returned so that we don't incur
  // any unnecessary renders.
  const form = useMemo(() => {
    return {
      handleReset,
      handleSubmit,
      dirty: state.dirty.size > 0,
      valid: state.errors.size === 0,
      submitting: state.submitting,
      [CONTROLLER]: {
        state,
        dispatch,
      },
    };
  }, [state, dispatch]);

  return form;
}

// Shape of properties that can be spread into a native object.
export interface InputHelpers<T, K extends keyof T> {
  onBlur: (_event: FormEvent<any>) => void;
  onChange: (event: ChangeEvent<any & { value: T[K] }>) => void;
  onFocus: (_event: FormEvent<any>) => void;
  value: T[K];
}

// Shape of properties that describe the state of a field which native elements
// don't understand but we can use to render UI.
export interface MetaHelpers {
  dirty?: boolean;
  errors?: string[] | null;
  focused?: boolean;
  touched?: boolean;
}

export interface Handlers<T> {
  setValue: (value: T) => void;
}

// 🎉 Special helpers to easily spread all form data into an Iris component
// in one line. Magic.
export interface IrisHelpers<T, K extends keyof T> extends InputHelpers<T, K> {
  format: 'negative' | 'neutral';
  errorMsg: ReactNode | undefined;
}

function useField<T, K extends keyof T>({ [CONTROLLER]: { state, dispatch } }: Form<T>, name: K) {
  // Memoized callbacks.
  const onBlur = useCallback(() => dispatch({ type: 'BLUR', payload: { name } }), [dispatch, name]);
  const onChange = useCallback(
    (event: ChangeEvent<any & { value: T[K] }>) => {
      dispatch({
        type: 'UPDATE',
        payload: { name, value: event.currentTarget.value },
      });
    },
    [dispatch, name],
  );
  const onFocus = useCallback(
    () => dispatch({ type: 'FOCUS', payload: { name } }),
    [dispatch, name],
  );

  // Memoized input object to spread into native elements.
  const input = useMemo<InputHelpers<T, K>>(() => {
    return {
      onBlur,
      onChange,
      onFocus,
      value: state.draft[name],
    };
  }, [state.draft[name]]);

  // Memoized data that describes the field in ways that form elements don't
  // understand.
  const meta = useMemo<MetaHelpers>(() => {
    return {
      dirty: state.dirty.get(name),
      errors: state.errors.get(name),
      focused: state.focused.get(name),
      touched: state.touched.get(name),
    };
  }, [
    state.dirty.get(name),
    state.touched.get(name),
    state.errors.get(name),
    state.focused.get(name),
  ]);

  // Memoized iris helper.
  const iris = useMemo<IrisHelpers<T, K>>(() => {
    const error = state.errors.get(name);
    const isDirty = state.dirty.get(name);
    return {
      ...input,
      format: isDirty && error ? 'negative' : 'neutral',
      errorMsg: isDirty && error ? error : undefined,
    };
  }, [input, meta]);

  // Memoized handlers, these provide raw and direct access to the inner state.
  // These never change so the second param of useMemo here is an empty object,
  // it never recalculates.
  const handlers = useMemo<Handlers<T[K]>>(() => {
    return {
      setValue: (value: T[K]) => dispatch({ type: 'UPDATE', payload: { name, value } }),
    };
  }, []);

  // For good measure, we memoize the field result before returning it.
  // 🔥 Blazing fast™.
  const field = useMemo(() => {
    return { input, meta, handlers, iris };
  }, [input, iris, meta]);

  return field;
}

export { useForm, useField };
